/**
 * State-aware "Open in Cursor" workspace actions menu.
 *
 * Replaces the bare OpenInCursorButton icon with a menu whose items reflect whether the
 * todo's project is resolvable, has a configured git path, or is ready to open with code.
 * See docs/architecture (workspace actions menu redesign plan) for the state diagram this
 * component implements.
 */

import { useCallback, useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from "@/lib/menu-chrome"
import {
  Code2,
  ChevronDown,
  Loader2,
  Settings,
  Link2,
  Download,
  Copy,
} from "lucide-react"
import { toast } from "sonner"
import { fetchWithAuth } from "@/lib/api"
import { buildMaterializeUri, launchCursorUri } from "@/lib/launch-cursor-uri"
import { cn } from "@/lib/utils"
import { testIds } from "@/lib/test-ids"
import { useOpenInCursor, type ProjectNotLinkedInfo } from "@/hooks/use-open-in-cursor"
import { WorkspaceLinkDialog } from "./workspace-link-dialog"
import { GitPathConfig } from "@/components/integrations/basecamp/components/git-path-config"

type GitState = "idle" | "loading" | "ready" | "no-git" | "unlinked"

export interface CursorWorkspaceMenuProps {
  todoId?: string | null
  todoTitle?: string | null
  projectName?: string | null
  projectSlug?: string | null
  /** Basecamp bucket/project id - lets the menu resolve state even when projectName is unavailable. */
  basecampProjectId?: string | null
  documentId?: string | null
  /** Content-relative folder path of the folder currently being browsed, for "Materialize locally". */
  folderPath?: string | null
  /** @deprecated Trigger is always icon-only; kept so existing callers compile. */
  compact?: boolean
  className?: string
  "data-testid"?: string
}

export function CursorWorkspaceMenu({
  todoId,
  todoTitle,
  projectName,
  projectSlug,
  basecampProjectId,
  documentId,
  folderPath,
  className,
  "data-testid": dataTestId,
}: CursorWorkspaceMenuProps) {
  const [gitState, setGitState] = useState<GitState>("idle")
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkContext, setLinkContext] = useState<ProjectNotLinkedInfo | null>(null)

  const canResolveProject = Boolean(projectName || projectSlug || basecampProjectId)

  const openLinkDialog = useCallback(
    (info?: Partial<ProjectNotLinkedInfo>) => {
      setLinkContext({
        projectName: info?.projectName || projectName || todoTitle || "This todo",
        basecampProjectId: info?.basecampProjectId ?? basecampProjectId ?? null,
      })
      setLinkDialogOpen(true)
    },
    [projectName, todoTitle, basecampProjectId],
  )

  const { isGenerating, handleOpenInCursor } = useOpenInCursor({
    todoId,
    todoTitle,
    projectName,
    projectSlug,
    basecampProjectId,
    documentId,
    onProjectNotLinked: openLinkDialog,
  })

  const refreshGitStatus = useCallback(async () => {
    if (!canResolveProject) {
      setGitState("unlinked")
      return
    }
    setGitState("loading")
    try {
      const params = new URLSearchParams()
      if (projectSlug) params.set("projectSlug", projectSlug)
      if (projectName) params.set("projectName", projectName)
      if (basecampProjectId) params.set("basecampProjectId", String(basecampProjectId))
      const res = await fetchWithAuth(`/api/workspace/git-status?${params}`)
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean
        hasGitPath?: boolean
      }
      if (!res.ok) {
        if (res.status === 503) {
          toast.warning("Can't check git path yet", {
            description: "PostgreSQL didn't respond. Confirm Docker Postgres is up, then reopen this menu.",
          })
        }
        setGitState("ready") // unknown - don't block the menu on a transient failure
        return
      }
      setGitState(data?.success && data.hasGitPath ? "ready" : data?.success ? "no-git" : "ready")
    } catch {
      setGitState("ready")
    }
  }, [canResolveProject, projectSlug, projectName, basecampProjectId])

  // Re-check after a git path is saved elsewhere (GitPathConfig dispatches this on save).
  useEffect(() => {
    const onSaved = (ev: Event) => {
      const pid = (ev as CustomEvent<{ projectId?: string }>).detail?.projectId
      if (pid != null && basecampProjectId != null && String(pid) === String(basecampProjectId)) {
        void refreshGitStatus()
      }
    }
    window.addEventListener("flownotes-basecamp-git-config-saved", onSaved as EventListener)
    return () => window.removeEventListener("flownotes-basecamp-git-config-saved", onSaved as EventListener)
  }, [basecampProjectId, refreshGitStatus])

  const handleOpenChange = (open: boolean) => {
    if (open && gitState === "idle") {
      void refreshGitStatus()
    }
  }

  const handleConfigureGitRepo = () => {
    if (basecampProjectId) {
      window.dispatchEvent(
        new CustomEvent("basecamp-project-configure-git", { detail: { projectId: basecampProjectId } }),
      )
    } else {
      openLinkDialog()
    }
  }

  const handleMaterializeLocally = () => {
    if (!folderPath) return
    const uri = buildMaterializeUri(folderPath)
    launchCursorUri(uri)
    toast.info("Check Cursor - the folder is being downloaded and revealed", {
      duration: 6000,
    })
  }

  if (!todoId) return null

  const isCheckingGit = gitState === "idle" || gitState === "loading"

  return (
    <>
      <DropdownMenu onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isGenerating}
            className={cn(
              "h-8 px-2 gap-1 text-muted-foreground hover:text-foreground shrink-0",
              className,
            )}
            title="Cursor workspace actions"
            data-testid={dataTestId ?? testIds.cursorWorkspaceMenu.trigger}
          >
            {isGenerating ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Code2 className="h-3 w-3" />
            )}
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
          collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
        >
          {isCheckingGit && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Checking project status...
            </div>
          )}

          {gitState === "unlinked" && (
            <DropdownMenuItem
              onClick={() => openLinkDialog()}
              data-testid={testIds.cursorWorkspaceMenu.linkProject}
            >
              <Link2 className="mr-2 h-4 w-4" />
              Link project...
            </DropdownMenuItem>
          )}

          {gitState === "no-git" && (
            <>
              <DropdownMenuItem
                onClick={handleConfigureGitRepo}
                data-testid={testIds.cursorWorkspaceMenu.configureGitRepository}
              >
                <Settings className="mr-2 h-4 w-4" />
                Configure git repository...
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => void handleOpenInCursor()}
                disabled={isGenerating}
                data-testid={testIds.cursorWorkspaceMenu.openTaskContextOnly}
              >
                {isGenerating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Code2 className="mr-2 h-4 w-4" />
                )}
                Open in Cursor (task context only)
              </DropdownMenuItem>
            </>
          )}

          {gitState === "ready" && (
            <>
              <DropdownMenuItem
                onClick={() => void handleOpenInCursor()}
                disabled={isGenerating}
                data-testid={testIds.cursorWorkspaceMenu.openInCursor}
              >
                {isGenerating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Code2 className="mr-2 h-4 w-4" />
                )}
                Open in Cursor
              </DropdownMenuItem>
              {folderPath && (
                <DropdownMenuItem
                  onClick={handleMaterializeLocally}
                  data-testid={testIds.cursorWorkspaceMenu.materializeLocally}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Materialize locally
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => void handleOpenInCursor({ launch: false })}
                disabled={isGenerating}
                data-testid={testIds.cursorWorkspaceMenu.copyWorkspaceUri}
              >
                <Copy className="mr-2 h-4 w-4" />
                Copy workspace URI
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleConfigureGitRepo}
                data-testid={testIds.cursorWorkspaceMenu.configureGitRepository}
              >
                <Settings className="mr-2 h-4 w-4" />
                Configure git repository...
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Hidden GitPathConfig - listens for 'basecamp-project-configure-git' dispatched above. */}
      {basecampProjectId && (
        <div className="hidden">
          <GitPathConfig
            projectId={basecampProjectId}
            projectName={projectName || todoTitle || "Project"}
            variant="badge"
            onUpdate={() => void refreshGitStatus()}
          />
        </div>
      )}

      {linkContext && (
        <WorkspaceLinkDialog
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
          projectName={linkContext.projectName || todoTitle || "This todo"}
          basecampProjectId={linkContext.basecampProjectId}
          onLinked={() => {
            setGitState("idle")
            void handleOpenInCursor()
          }}
        />
      )}
    </>
  )
}
